import type { Getter } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import Axios from "axios";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import styled, { ThemeContext } from "styled-components";
import useSWR from "swr";
import { useAuthContext } from "../../../../../components/Auth";
import { TertiaryButtonMediumV2 } from "../../../../../components/Buttons/Buttons";
import {
  DeleteIcon,
  EditIcon,
  NonVisibleIcon,
  VisibleIcon,
} from "../../../../../components/Icons/Icons";
import { Row } from "../../../../../components/Layout/Layout";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { Table } from "../../../../../components/Table/Table";
import { WithPermission } from "../../../../../components/WithPermission/WithPermission";
import { endpoints } from "../../../../../endpoints";
import type {
  AnalyticsProp,
  IPagination,
  WithPagination,
} from "../../../../../types/types";
import type {
  AssetsExternalLinks,
  PIMProduct,
} from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import {
  defaultHandleSort,
  formatDate,
  TablePlaceholder,
  useStoreState,
} from "../../../../../util/util";
import {
  ScrollTableWrapper,
  TableFiltersWrapper,
} from "../DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { AssetLink } from "../util";
import { AddandEditLink } from "./AddandEditLink";
import { DeleteExternalLink } from "./DeleteExternalLink";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import type { AssetContainerType } from "../../../../../util/assetContainer.types";
import { getExternalLinksListEndpoint } from "../../../../../util/assetEndpoints";
import { spacingTokens } from "../../../../../designTokens";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";

const TableContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  & div[class*="TableWrapper"] {
    overflow: visible;
    td {
      overflow: visible;
    }
  }
`;

export const ExternalLinkSearchContainer = styled.div`
  border-left: ${({ theme }) =>
    `${theme.resolve("border_3xl")} solid ${theme.secondaryBorder}`};
  border-top: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  border-right: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  padding: 5px 10px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const BtnWrapper = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
`;

type TableProduct = {
  name: string;
  url: string;
};

const LIMIT = 20;

export const ExternalLinksTable = ({
  product,
  assetContainerType = "product",
  assetContainerId,
}: {
  product: PIMProduct;
  assetContainerType?: AssetContainerType;
  assetContainerId?: string;
}) => {
  const { t } = useTranslation();
  const theme = useContext(ThemeContext);
  const { notifySuccess, notifyError } = useContext(Notifications);

  const {
    storefront_metadata: { enable_product_asset_search },
    tenant_id,
  } = useStoreState();
  const { hasPermission, roleIsSomeKindOfSeller } = useAuthContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSlideOut, setSlideOut] =
    useState<"add" | "edit" | "delete" | false>(false);

  const [tableData, setTableData] = useState<TableProduct[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [linksData, setLinksData] = useState<AssetsExternalLinks[]>([]);
  const [pagination, setPagination] = useState<IPagination>();
  const [editRowData, setEditRowData] = useState<any>();
  const [deleteRowData, setDeleteRowData] = useState<any>();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [offset, setOffset] = useState(0);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const resolvedAssetContainerId = assetContainerId ?? product.id;
  const externalLinksEndpoint = getExternalLinksListEndpoint(
    assetContainerType,
    tenant_id,
    resolvedAssetContainerId
  );

  const {
    data: external_links,
    error,
    mutate,
    isValidating,
  } = useSWR<WithPagination<{ data: AssetsExternalLinks[] }>>([
    externalLinksEndpoint,
    useMemo(
      () => ({
        params: {
          ...(debouncedSearchQuery ? { q: debouncedSearchQuery } : {}),
          offset,
          sort_by: sortingRules.sortBy,
          order_by: sortingRules.orderBy,
          limit: LIMIT,
        },
      }),
      [debouncedSearchQuery, offset, sortingRules.orderBy, sortingRules.sortBy]
    ),
  ]);

  const closeSlideOut = () => setSlideOut(false);
  const closeDeleteModal = () => setShowDeleteModal(false);

  const handleChangeVisibility = useCallback(
    (original) => {
      (async (original: AssetsExternalLinks) => {
        try {
          const linkId = original?.id;
          const isVisible = original?.is_visible;
          const patchBaseUrl =
            endpoints.v2_storefronts_id_pim_assets_external_links_id(
              tenant_id,
              linkId
            );

          await Axios.patch(patchBaseUrl, { is_visible: !isVisible });
          await mutate();
          notifySuccess(t("External link successfully Edited"));
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage
              ? t("{{axios_error_message}}", {
                  axios_error_message: errorMessage,
                })
              : t("Could not edit an asset. Something went wrong."),
            {
              error,
            }
          );
        }
      })(original);
    },
    [mutate, notifyError, notifySuccess, t, tenant_id]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const handleDelete = (original: AssetsExternalLinks) => {
    setShowDeleteModal(true);
    setDeleteRowData(original);
  };

  const handleEdit = (original: AssetsExternalLinks) => {
    setSlideOut("edit");
    setEditRowData(original);
  };

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      if (
        scrollRef.current.offsetHeight - scrollTop < 400 &&
        !isLoadingData &&
        ((pagination?.total && linksData.length < pagination.total) ||
          !pagination) &&
        !isValidating
      ) {
        setIsLoadingData(true);
        setOffset(offset + LIMIT);
      }
    }
  };

  const tableColumns = React.useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        cell: ({
          getValue,
          row: {
            original: { url },
          },
        }: {
          getValue: Getter<string>;
          row: {
            original: {
              url: string;
            };
          };
        }) => {
          const analytics: AnalyticsProp = {
            analyticsName: getValue(),
            objectId: product.product_number ?? product.id,
            eventType: "product_link",
            link: url,
          };
          return (
            <AssetLink
              analytics={analytics}
              url={url}
              name={getValue()}
              is_visible={true}
            />
          );
        },
      },
      {
        header: t("Last Modified"),
        accessorKey: "modified_at",
      },
      {
        header: t("Last Modified By"),
        accessorKey: "modified_by",
        enableSorting: false,
      },
      {
        header: product.status !== "archived" ? t("Actions") : "",
        accessorKey: "is_visible",
        enableSorting: false,
        cell: ({
          row: { original },
          getValue,
        }: {
          row: { original: AssetsExternalLinks };
          getValue: () => boolean;
        }) =>
          product.status !== "archived" &&
          product.is_editable && (
            <IconWrapper>
              {original.is_editable &&
                hasPermission("modify_assets") &&
                (product.pim_product_type === "variant"
                  ? !original.is_master
                  : true) && (
                  <BtnWrapper
                    data-testid={`edit-btn-${original.id}`}
                    onClick={() => handleEdit(original)}
                  >
                    <EditIcon width={20} height={20} />
                  </BtnWrapper>
                )}
              {getValue() &&
                hasPermission("modify_assets") &&
                (product.pim_product_type === "variant"
                  ? !original.is_master
                  : true) && (
                  <BtnWrapper
                    data-testid={`visibility-btn-${original.id}`}
                    onClick={() => handleChangeVisibility(original)}
                  >
                    <VisibleIcon
                      fill={theme.successIconColor}
                      width={20}
                      height={20}
                    />
                  </BtnWrapper>
                )}
              {!getValue() &&
                hasPermission("modify_assets") &&
                (product.pim_product_type === "variant"
                  ? !original.is_master
                  : true) && (
                  <BtnWrapper
                    data-testid={`visibility-btn-${original.id}`}
                    onClick={() => handleChangeVisibility(original)}
                  >
                    <NonVisibleIcon
                      fill={theme.errorColor}
                      width={20}
                      height={20}
                    />
                  </BtnWrapper>
                )}
              {original.is_editable &&
                hasPermission("delete_assets") &&
                (product.pim_product_type === "variant"
                  ? !original.is_master
                  : true) && (
                  <BtnWrapper
                    data-testid={`delete-btn-${original.id}`}
                    onClick={() => handleDelete(original)}
                  >
                    <DeleteIcon fill={theme.errorColor} />
                  </BtnWrapper>
                )}
            </IconWrapper>
          ),
      },
    ],
    [
      t,
      product.status,
      product.product_number,
      product.id,
      product.is_editable,
      product.pim_product_type,
      hasPermission,
      theme.successIconColor,
      theme.errorColor,
      handleChangeVisibility,
    ]
  );

  useEffect(() => {
    const handleProductsData = (data: AssetsExternalLinks[]) => {
      setTableData(
        data.reduce<
          (TableProduct &
            {
              [Prop in keyof AssetsExternalLinks]+?: AssetsExternalLinks[Prop];
            })[]
        >(
          (
            acc,
            {
              id,
              url,
              name,
              is_visible,
              modified_at,
              modified_by,
              is_editable,
              is_master,
            }
          ) => {
            acc.push({
              id,
              url,
              name: name ?? "--",
              modified_at: formatDate(modified_at) ?? "--",
              modified_by: modified_by ?? "--",
              is_visible,
              is_editable,
              is_master,
            });
            return acc;
          },
          []
        )
      );
    };
    handleProductsData(linksData);
  }, [linksData]);

  useEffect(() => {
    if (external_links?.data) {
      if (offset === 0) {
        setLinksData(external_links.data);
      } else {
        setLinksData((prev) => [...prev, ...external_links.data]);
      }
      setIsLoadingData(false);
      setPagination(external_links.pagination);
    }
  }, [external_links, offset]);

  const is_loading = !external_links && !error;

  const shouldApplySearchPadding = () => {
    if (!enable_product_asset_search) {
      return false;
    }
    if (roleIsSomeKindOfSeller) {
      return hasPermission(["view_products", "view_assets"], "every");
    }
    return true;
  };

  const mainSection = (
    <>
      <TableFiltersWrapper has_padding={shouldApplySearchPadding()}>
        {enable_product_asset_search && (
          <>
            {roleIsSomeKindOfSeller ? (
              <WithPermission
                permission={["view_assets", "view_products"]}
                permission_type="every"
              >
                <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                  <SearchBar
                    query={searchQuery}
                    placeHolder={t("Search by Link Name or URL")}
                    handleChange={handleSearch}
                    handleClearInput={handleClearSearch}
                    maxWidth="450px"
                  />
                </Row>
              </WithPermission>
            ) : (
              <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                <SearchBar
                  query={searchQuery}
                  placeHolder={t("Search by Link Name or URL")}
                  handleChange={handleSearch}
                  handleClearInput={handleClearSearch}
                  maxWidth="450px"
                />
              </Row>
            )}
          </>
        )}
      </TableFiltersWrapper>
      <TableContainer>
        <ScrollTableWrapper ref={scrollRef} onScroll={handleScroll}>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={is_loading}
            error={error}
            handleSort={handleSort}
            lastChildleftAlign
            Placeholder={<TablePlaceholder />}
          />
        </ScrollTableWrapper>
      </TableContainer>

      {hasPermission("modify_assets") &&
        product.status !== "archived" &&
        product.is_editable && (
          <TertiaryButtonMediumV2
            style={{ width: "fit-content" }}
            onClick={() => {
              setSlideOut("add");
            }}
          >
            <PlusIcon />
            {t("Add")}
          </TertiaryButtonMediumV2>
        )}
    </>
  );

  return (
    <div className={PROFILE_DETAIL_CLASSES.assetsTable}>
      <AccordionCard
        title={t("External Links")}
        subtitle={t("Manage External Links and control their accessibility")}
        expanded={true}
        actionChildren={() => <></>}
      >
        {mainSection}
      </AccordionCard>

      {showSlideOut === "add" && (
        <SlideOut
          closeFlyout={closeSlideOut}
          show={!!showSlideOut}
          title={t("Add external Link")}
        >
          <AddandEditLink
            formType="add"
            refreshAssetsList={mutate}
            onSuccess={closeSlideOut}
            onCancel={closeSlideOut}
            assetContainerType={assetContainerType}
            assetContainerId={resolvedAssetContainerId}
          />
        </SlideOut>
      )}

      {showSlideOut === "edit" && (
        <SlideOut
          closeFlyout={closeSlideOut}
          show={!!showSlideOut}
          title={t("Edit external Link")}
        >
          <AddandEditLink
            formType="edit"
            refreshAssetsList={mutate}
            editData={editRowData}
            onSuccess={closeSlideOut}
            onCancel={closeSlideOut}
            assetContainerType={assetContainerType}
            assetContainerId={resolvedAssetContainerId}
          />
        </SlideOut>
      )}

      {showDeleteModal && (
        <DeleteExternalLink
          onSuccess={closeDeleteModal}
          show={showDeleteModal}
          onClose={closeDeleteModal}
          refreshAssetsList={mutate}
          deleteData={deleteRowData}
        />
      )}
    </div>
  );
};
